<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { Team } from "@shared/types";
import Flag from "../common/Flag.vue";

const props = defineProps<{
  potNumber: 1 | 2 | 3;
  teams: Team[];
  targetId: string | null;
  duration: number;
  trigger: number;
}>();

const REPEATS = 6;
const TILE_PX = 120;

const stripTeams = computed(() => {
  const out: Team[] = [];
  for (let i = 0; i < REPEATS; i++) out.push(...props.teams);
  return out;
});

const offsetPx = ref<number>(0);
const spinning = ref(false);
const viewport = ref<HTMLElement | null>(null);

function landingOffset(targetId: string): number {
  const indexInPot = props.teams.findIndex(t => t.id === targetId);
  const indexInLastRepeat = (REPEATS - 1) * props.teams.length + indexInPot;
  const viewportWidth = viewport.value?.clientWidth ?? 0;
  const centerOffset = viewportWidth / 2 - TILE_PX / 2;
  const jitter = (Math.random() * 0.2 - 0.1) * TILE_PX;
  return -(indexInLastRepeat * TILE_PX) + centerOffset + jitter;
}

watch(() => props.trigger, () => {
  if (!props.targetId) return;
  spinning.value = false;
  offsetPx.value = -Math.floor(Math.random() * props.teams.length) * TILE_PX;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      spinning.value = true;
      offsetPx.value = landingOffset(props.targetId!);
    });
  });
});

const stripStyle = computed(() => ({
  transform: `translateX(${offsetPx.value}px)`,
  transitionDuration: spinning.value ? `${props.duration}s` : "0s",
}));

const indicatorColor = computed(() => ({
  1: "text-pot1",
  2: "text-pot2",
  3: "text-pot3",
}[props.potNumber]));
</script>

<template>
  <section class="mb-4">
    <div class="flex items-center justify-between mb-2">
      <h2 :class="['text-xs uppercase tracking-wider font-bold', indicatorColor]">
        Pot {{ props.potNumber }}
      </h2>
    </div>
    <div ref="viewport" :class="['reel-viewport', indicatorColor]">
      <div class="reel-strip" :data-spinning="spinning" :style="stripStyle">
        <div v-for="(team, i) in stripTeams" :key="i + team.id" class="reel-tile">
          <Flag :code="team.flag_code" :name="team.name" size="lg" />
          <span class="text-xs text-slate-700 mt-1 font-medium">{{ team.name }}</span>
        </div>
      </div>
      <div class="reel-indicator"></div>
    </div>
  </section>
</template>
